import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { MaskedLines, FadeUp, SectionTag } from "@/components/Reveal";
import { mailto } from "@/data/content";

const PersonProfile = ({ person, seoTitle, seoDescription, seoPath, seoLd, testPrefix }) => (
  <div data-testid={`${testPrefix}-page`}>
    <SEO title={seoTitle} description={seoDescription} path={seoPath} jsonLd={seoLd} image={person.image} />

    {/* HERO */}
    <section className="relative overflow-hidden pt-32 sm:pt-40">
      <div className="mx-auto grid max-w-7xl items-end gap-10 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-6 lg:pb-20">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mb-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-rust"
          >
            <span className="h-px w-10 bg-rust" /> {person.role}
          </motion.p>
          <MaskedLines
            lines={[person.name.split(" ")[0], person.name.split(" ")[1]]}
            className="font-serif text-6xl leading-[1.02] sm:text-8xl"
            delay={0.3}
          />
          <FadeUp delay={0.7}>
            <p className="mt-8 max-w-lg text-lg leading-relaxed text-stone">{person.intro}</p>
            <p className="mt-8 border-l-2 border-rust pl-5 font-accent text-2xl italic leading-snug text-ink">
              {person.quote}
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.4} className="lg:col-span-5 lg:col-start-8">
          <div className="relative">
            <div className="overflow-hidden border border-sand">
              <motion.img
                initial={{ scale: 1.12 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                src={person.image}
                alt={`Porträt von ${person.name}`}
                style={{ objectPosition: person.imagePos || "center" }}
                className="aspect-[3/4] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-sand bg-creme px-6 py-4 sm:block">
              <p className="font-serif text-lg">{person.name}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-stone">{person.role}</p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>

    {/* BIO */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
      <div className="grid gap-14 lg:grid-cols-12">
        <FadeUp className="lg:col-span-4">
          <SectionTag>Mein Weg</SectionTag>
          <h2 className="mt-5 font-serif text-4xl leading-[1.1]">
            Nicht ein Lebenslauf –<br />
            <span className="font-accent italic text-rust">ein Weg.</span>
          </h2>
          <div className="mt-10 overflow-hidden border border-sand">
            <img src={person.imageAlt} alt={`${person.name} – persönlicher Eindruck`} style={{ objectPosition: person.imageAltPos || "center" }} loading="lazy" className="aspect-[4/3] w-full object-cover" />
          </div>
        </FadeUp>
        <div className="lg:col-span-7 lg:col-start-6">
          {person.bio.map((p, i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <p className="mb-7 text-lg leading-relaxed text-stone first:text-xl first:text-ink">{p}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* VALUES & SKILLS */}
    <section className="bg-parch py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <FadeUp>
          <SectionTag>Woran ich glaube</SectionTag>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl">Meine Werte</h2>
          <ul className="mt-8 space-y-5">
            {person.values.map((v, i) => (
              <li key={v} className="flex items-center gap-4 border-b border-sand/70 pb-5">
                <span className="font-accent text-2xl italic text-sand">0{i + 1}</span>
                <span className="font-serif text-xl">{v}</span>
              </li>
            ))}
          </ul>
        </FadeUp>
        <FadeUp delay={0.15}>
          <SectionTag>Woran ich besonders gut bin</SectionTag>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl">Erfahrung & Stärken</h2>
          <ul className="mt-8 space-y-4">
            {person.skills.map((s) => (
              <li key={s} className="flex items-start gap-3 text-stone">
                <Check size={18} className="mt-1 shrink-0 text-moss" />
                <span className="leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </FadeUp>
      </div>
    </section>

    {/* PERSONAL Q&A */}
    <section className="mx-auto max-w-5xl px-5 py-24 sm:px-8 sm:py-32">
      <FadeUp className="mb-12 text-center">
        <SectionTag>Persönlich gefragt</SectionTag>
        <h2 className="mt-5 font-serif text-4xl">Vier Fragen, vier ehrliche Antworten</h2>
      </FadeUp>
      <div className="grid gap-6 sm:grid-cols-2">
        {person.facts.map((f, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <div data-testid={`${testPrefix}-fact-${i}`} className="h-full rounded-2xl border border-sand bg-parch/60 p-7">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rust">{f.q}</p>
              <p className="mt-3 font-accent text-xl italic leading-snug text-ink">{f.a}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp className="mt-14 text-center">
        <a
          href={mailto(`Gespräch mit ${person.name}`)}
          data-testid={`${testPrefix}-cta-button`}
          className="inline-flex items-center gap-3 rounded-full bg-moss px-10 py-4 text-sm font-bold text-creme transition-all duration-300 hover:scale-[1.03] hover:bg-mossdeep"
        >
          {person.cta} <ArrowRight size={16} />
        </a>
        <p className="mt-4 text-xs text-stone">Unverbindlich, persönlich und ohne Verkaufsdruck.</p>
      </FadeUp>
    </section>

    <CTASection
      title={["Noch Fragen?", "Schreiben Sie uns einfach."]}
      text={`Ob es um eine Trauung, eine Willkommensfeier oder ein Jubiläum geht – ${person.name.split(" ")[0]} und das Team melden sich persönlich bei Ihnen zurück.`}
      buttonLabel="Unverbindlich Kontakt aufnehmen"
    />
  </div>
);

export default PersonProfile;
